<template>
    <admin-page :result="result" description="Documento config/tutorials, impacta a todas las apps al instante">
        <skeleton-loader v-if="loading" type="rows" :count="5" />

        <template v-else>
            <div class="config-row">
                <div class="config-row-body">
                    <div class="config-name">Tutoriales activos</div>
                    <div class="config-desc">Apagado oculta el botón de tutoriales en toda la app.</div>
                </div>
                <kimsa-toggle :value="active" @change="onToggleActive" />
            </div>

            <div class="list-head mt-6">
                <div class="config-section-title mb-0">Tutoriales ({{ items.length }})</div>
                <div class="list-head-actions">
                    <kimsa-text-field class="list-search" :value="search" @change="search = $event" dense hide-details
                        placeholder="Buscar" prepend-inner-icon="mdi-magnify" />
                    <v-btn color="primary" class="black--text config-btn" depressed @click="create">
                        Nuevo tutorial
                    </v-btn>
                </div>
            </div>
            <div class="config-list">
                <div v-for="group in groups" :key="group.key" class="config-group-wrap">
                    <button type="button" class="config-group" @click="toggleGroup(group.key)">
                        <v-icon size="16" class="config-group-chevron" :class="{ open: isGroupOpen(group) }">
                            mdi-chevron-right
                        </v-icon>
                        {{ group.label }}
                        <span class="config-group-count">({{ group.items.length }})</span>
                    </button>

                    <template v-if="isGroupOpen(group)">
                        <div v-for="item in group.items" :key="item.slug" class="config-row">
                            <div class="config-row-body">
                                <div class="config-name">
                                    <span class="config-order">{{ item.order || 0 }}</span>
                                    {{ item.title && item.title.es || item.slug }}
                                </div>
                                <div class="config-desc">
                                    {{ item.slug }} · {{ featureLabel(item.feature) }} ·
                                    {{ screensLabel(item) }} ·
                                    {{ accountTypesLabel(item) }}{{ item.active === false ? ' · inactivo' : '' }}
                                </div>
                            </div>
                            <div class="row-actions">
                                <button-tooltip top tooltip="Editar">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="icon-btn" v-on="on" v-bind="attrs" @click="edit(item)">
                                            <v-icon size="15">mdi-pencil-outline</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                                <button-tooltip top tooltip="Eliminar">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="icon-btn danger-btn" v-on="on" v-bind="attrs"
                                            @click="confirmDelete(item)">
                                            <v-icon size="15">$delete</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                            </div>
                        </div>
                    </template>
                </div>
                <div v-if="!filteredItems.length" class="config-empty">
                    {{ items.length ? 'Ningún tutorial coincide con la búsqueda.' : 'Todavía no hay tutoriales cargados.' }}
                </div>
            </div>

        </template>

        <base-dialog :loading="loading" :value="!!form" :width="720" :fullscreen="isMobile" :title="formTitle" @input="onDialogInput"
            @close="form = null">
            <template v-if="form">
                <div class="tutorial-form">
                    <div class="form-group">
                        <span class="field-label">Slug</span>
                        <kimsa-text-field :value="form.slug" @change="form.slug = $event" :readonly="!isNew" dense
                            placeholder="zapping-basico" />
                    </div>
                    <div class="form-group">
                        <span class="field-label">Función</span>
                        <kimsa-combobox :items="featureOptions" :value="form.feature" @change="form.feature = $event"
                            item-value="value" no-object dense clearable placeholder="Sin función asociada" />
                    </div>
                    <div class="form-group">
                        <span class="field-label">Pantallas</span>
                        <kimsa-combobox :items="screenOptions" :value="form.screens" @change="form.screens = $event"
                            item-value="value" no-object multiple-with-chips show-close-button dense
                            placeholder="Todas" />
                    </div>
                    <div class="form-group">
                        <span class="field-label">Tipos de cuenta</span>
                        <kimsa-combobox :items="accountTypeOptions" :value="form.accountTypes"
                            @change="form.accountTypes = $event" item-value="value" no-object multiple-with-chips
                            show-close-button dense placeholder="Todos" />
                    </div>
                    <div class="form-group">
                        <span class="field-label">Orden</span>
                        <kimsa-text-field :value="form.order" @change="form.order = $event" dense type="number" />
                    </div>
                    <div class="form-group form-group--toggle">
                        <span class="field-label">Activo</span>
                        <kimsa-toggle :value="form.active" @change="form.active = $event" />
                    </div>
                    <div v-for="field in localizedFields" :key="field.key" class="form-group form-group--wide">
                        <span class="field-label">{{ field.name }}</span>
                        <div class="form-row">
                            <kimsa-text-field :value="form[field.key].es" @change="form[field.key].es = $event" dense
                                placeholder="Español" />
                            <kimsa-text-field :value="form[field.key].en" @change="form[field.key].en = $event" dense
                                placeholder="Inglés (opcional)" />
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:footer>
                <dialog-actions confirm-label="Guardar tutorial" cancel-label="Cancelar" :disabled="!valid"
                    :loading="saving" @cancel="form = null" @confirm="save" />
            </template>
        </base-dialog>

        <kimsa-confirm ref="confirmDelete" alert-mode show-cancel title="Eliminar tutorial" :content="deleteContent"
            :persistent="false" :close-on-confirm="false" :loading="saving" @confirm="remove" />
    </admin-page>
</template>

<script>
import KimsaToggle from '@/components/common/inputs/KimsaToggle.vue'
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaCombobox from '@/components/common/inputs/KimsaCombobox.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip.vue'
import BaseDialog from '@/components/common/BaseDialog.vue'
import DialogActions from '@/components/common/DialogActions.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { mapActions } from 'vuex'
import { db, FieldValue } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'
import { FEATURES } from '@/utils/Plans'

const SCREEN_LABELS = {
    home: 'Inicio',
    students: 'Alumnos',
    library: 'Biblioteca',
    calendar: 'Calendario',
    statistics: 'Estadísticas',
    evaluations: 'Evaluaciones',
    admin: 'Admin del gimnasio',
    profile: 'Perfil',
    routines: 'Rutinas (alumno)',
    chats: 'Chats (alumno)',
    coaches: 'Entrenadores (alumno)',
    business: 'Negocio',
}
const SCREENS = Object.keys(SCREEN_LABELS)
const ACCOUNT_TYPE_LABELS = { coach: 'Entrenador', company: 'Empresa', student: 'Alumno' }
const ACCOUNT_TYPES = Object.keys(ACCOUNT_TYPE_LABELS)
const LOCALIZED_FIELDS = [
    { key: 'title', name: 'Título' },
    { key: 'video', name: 'Video (m3u8)' },
    { key: 'thumb', name: 'Miniatura' },
]

export default {
    name: 'AdminTutorials',
    components: {
        KimsaToggle, KimsaTextField, KimsaCombobox, KimsaConfirm,
        SkeletonLoader, ButtonTooltip, BaseDialog, DialogActions, AdminPage,
    },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            active: false,
            items: [],
            search: '',
            openGroups: {},
            form: null,
            isNew: false,
            pendingDelete: null,
            localizedFields: LOCALIZED_FIELDS,
            loading: false,
            saving: false,
            result: null,
        }
    },
    computed: {
        filteredItems(vm = this) {
            const query = vm.search.trim().toLowerCase()
            if (!query) return vm.items
            return vm.items.filter(item => {
                const haystack = [item.slug, item.title?.es, item.title?.en, item.feature, ...(item.screens || [])]
                return haystack.some(value => (value || '').toLowerCase().includes(query))
            })
        },
        groups(vm = this) {
            const groups = []
            vm.filteredItems.forEach(item => {
                const key = vm.screenKey(item)
                let group = groups.find(el => el.key === key)
                if (!group) {
                    group = { key, label: SCREEN_LABELS[key] || key || 'Sin pantalla', items: [] }
                    groups.push(group)
                }
                group.items.push(item)
            })
            return groups
        },
        formTitle(vm = this) {
            return vm.isNew ? 'Nuevo tutorial' : `Editar ${vm.form?.slug || ''}`
        },
        featureOptions() {
            return Object.keys(FEATURES).sort().map(value => ({ value, name: this.$t(`plans.features.${value}`) }))
        },
        screenOptions() {
            return SCREENS.map(value => ({ value, name: SCREEN_LABELS[value] }))
        },
        accountTypeOptions() {
            return ACCOUNT_TYPES.map(value => ({ value, name: ACCOUNT_TYPE_LABELS[value] }))
        },
        valid(vm = this) {
            return !!vm.form?.slug && !!vm.form?.title?.es && !!vm.form?.video?.es
        },
        deleteContent(vm = this) {
            return `Vas a eliminar el tutorial <b>${vm.pendingDelete?.slug || ''}</b>.`
        },
    },
    mounted() {
        this.loadTutorials()
    },
    methods: {
        ...mapActions(['fetchTutorials']),
        async loadTutorials() {
            const vm = this
            await vm.withLoading(async () => {
                const doc = await db.collection('config').doc('tutorials').get()
                const data = doc.data() || {}
                vm.active = data.active === true
                const items = data.items || {}
                vm.items = Object.keys(items)
                    .map(slug => ({ slug, ...items[slug] }))
                    .sort((a, b) => {
                        const byScreen = vm.screenRank(a) - vm.screenRank(b)
                        if (byScreen) return byScreen
                        return (a.order || 0) - (b.order || 0) || a.slug.localeCompare(b.slug)
                    })
            })
        },
        onDialogInput(val) {
            if (!val) this.form = null
        },
        isGroupOpen(group) {
            if (this.search.trim()) return true
            return this.openGroups[group.key] === true
        },
        toggleGroup(key) {
            this.$set(this.openGroups, key, !this.openGroups[key])
        },
        screenKey(item) {
            return (item.screens || [])[0] || ''
        },
        screenRank(item) {
            const index = SCREENS.indexOf(this.screenKey(item))
            return index === -1 ? SCREENS.length : index
        },
        screensLabel(item) {
            const screens = item.screens || []
            if (!screens.length) return 'todas las pantallas'
            return screens.map(screen => SCREEN_LABELS[screen] || screen).join(', ')
        },
        accountTypesLabel(item) {
            const types = item.accountTypes || []
            if (!types.length) return 'todas las cuentas'
            return types.map(type => ACCOUNT_TYPE_LABELS[type] || type).join(', ')
        },
        featureLabel(feature) {
            if (!feature) return 'sin función'
            return this.$t(`plans.features.${feature}`)
        },
        emptyForm() {
            return {
                slug: '',
                feature: null,
                screens: [],
                accountTypes: [],
                order: this.items.length + 1,
                active: true,
                title: { es: '', en: '' },
                video: { es: '', en: '' },
                thumb: { es: '', en: '' },
            }
        },
        create() {
            const vm = this
            vm.isNew = true
            vm.form = vm.emptyForm()
        },
        edit(item) {
            const vm = this
            vm.isNew = false
            vm.form = {
                ...vm.emptyForm(),
                ...item,
                screens: [...(item.screens || [])],
                accountTypes: [...(item.accountTypes || [])],
                active: item.active !== false,
                title: { es: '', en: '', ...(item.title || {}) },
                video: { es: '', en: '', ...(item.video || {}) },
                thumb: { es: '', en: '', ...(item.thumb || {}) },
            }
        },
        confirmDelete(item) {
            const vm = this
            vm.pendingDelete = item
            vm.$refs.confirmDelete.show()
        },
        async ensureDoc() {
            const ref = db.collection('config').doc('tutorials')
            const doc = await ref.get()
            if (!doc.exists) await ref.set({ active: true, items: {} })
            return ref
        },
        async onToggleActive(value) {
            const vm = this
            vm.saving = true
            try {
                const ref = await vm.ensureDoc()
                await ref.update({ active: value })
                vm.active = value
                vm.result = { type: 'success', message: `Tutoriales ${value ? 'activados' : 'desactivados'}.` }
                vm.fetchTutorials(true)
            } catch (err) {
                console.error('[adminTutorials] active', err)
                vm.result = { type: 'error', message: err.message || 'Error al guardar.' }
            } finally {
                vm.saving = false
            }
        },
        buildPayload(slug) {
            const vm = this
            const base = `items.${slug}`
            const payload = {
                [`${base}.feature`]: vm.form.feature || null,
                [`${base}.screens`]: vm.form.screens || [],
                [`${base}.accountTypes`]: vm.form.accountTypes || [],
                [`${base}.order`]: Number(vm.form.order) || 0,
                [`${base}.active`]: vm.form.active !== false,
            }
            LOCALIZED_FIELDS.forEach(field => {
                payload[`${base}.${field.key}.es`] = vm.form[field.key].es || null
                payload[`${base}.${field.key}.en`] = vm.form[field.key].en || null
            })
            return payload
        },
        async save() {
            const vm = this
            vm.saving = true
            try {
                const slug = vm.form.slug.trim()
                const ref = await vm.ensureDoc()
                await ref.update(vm.buildPayload(slug))
                vm.result = { type: 'success', message: `Tutorial ${slug} guardado.` }
                vm.form = null
                await vm.loadTutorials()
                vm.fetchTutorials(true)
            } catch (err) {
                console.error('[adminTutorials] save', err)
                vm.result = { type: 'error', message: err.message || 'Error al guardar el tutorial.' }
            } finally {
                vm.saving = false
            }
        },
        async remove() {
            const vm = this
            vm.saving = true
            try {
                const slug = vm.pendingDelete.slug
                await db.collection('config').doc('tutorials')
                    .update({ [`items.${slug}`]: FieldValue.delete() })
                vm.result = { type: 'success', message: `Tutorial ${slug} eliminado.` }
                if (vm.form?.slug === slug) vm.form = null
                await vm.loadTutorials()
                vm.fetchTutorials(true)
            } catch (err) {
                console.error('[adminTutorials] remove', err)
                vm.result = { type: 'error', message: err.message || 'Error al eliminar el tutorial.' }
            } finally {
                vm.saving = false
                vm.pendingDelete = null
                vm.$refs.confirmDelete?.hide()
            }
        },
    },
}
</script>

<style scoped>
.config-section-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtSecondary-base);
    margin-bottom: 12px;
}

.config-list {
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.config-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.config-row:last-child {
    border-bottom: none;
}

.config-row-body {
    flex: 1;
    min-width: 0;
}

.config-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.config-desc {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
}

.list-head-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.list-search {
    width: 220px;
}

@media (max-width: 600px) {
    .list-head {
        flex-direction: column;
        align-items: stretch;
    }

    .list-search {
        width: auto;
        flex: 1;
    }
}

.config-group-wrap:not(:last-child) {
    border-bottom: 1px solid var(--v-outline-base);
}

.config-group {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    background: var(--v-surface2-base);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: color .15s;
}

.config-group:hover {
    color: var(--v-txtPrimary-base);
}

.config-group-chevron {
    color: var(--v-txtMuted-base) !important;
    transition: transform .15s;
}

.config-group-chevron.open {
    transform: rotate(90deg);
}

.config-group-count {
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.config-order {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    margin-right: 8px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 700;
}

.config-empty {
    padding: 20px 16px;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    text-align: center;
}

.row-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: border-color .15s, color .15s;
}

.icon-btn:hover {
    border-color: var(--v-txtSecondary-base);
    color: var(--v-txtPrimary-base);
}

.icon-btn.danger-btn:hover {
    border-color: var(--v-error-base);
    color: var(--v-error-base);
}

.tutorial-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}

.form-group--wide {
    grid-column: 1 / -1;
}

.form-group--toggle {
    display: flex;
    align-items: center;
    gap: 12px;
}

.form-row {
    display: flex;
    gap: 12px;
}

.form-row > * {
    flex: 1;
    min-width: 0;
}

.field-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.form-actions {
    display: flex;
    gap: 10px;
}

.config-btn {
    border-radius: 8px !important;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}

@media (max-width: 600px) {
    .tutorial-form {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
